import { useState, useEffect, useCallback, Component, ReactNode } from "react";
import { hardReload } from "@/lib/hard-reload";
import { useNavigate } from "react-router-dom";
import { captureWithContext } from "@/lib/sentry";

// Catches any render crash inside the app so the user sees an error message instead of blank
class AppErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    // Ship the crash to Sentry with the component stack so we can pinpoint which
    // subtree blew up. No-op when Sentry isn't configured.
    captureWithContext(error, { componentStack: info.componentStack ?? '(none)' });
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', fontFamily: 'sans-serif', background: '#f9fafb' }}>
          <div style={{ maxWidth: 480, textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, color: '#111' }}>Something went wrong</h2>
            <p style={{ color: '#555', fontSize: 14, marginBottom: 16 }}>{this.state.error.message}</p>
            <pre style={{ background: '#fee2e2', color: '#b91c1c', fontSize: 11, padding: '12px', borderRadius: 8, textAlign: 'left', overflowX: 'auto', marginBottom: 20 }}>{this.state.error.stack}</pre>
            <button onClick={() => { void hardReload(); }} style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
import { OnboardingWizard } from "@/components/onboarding/OnboardingWizard";
import { TeacherLogin } from "@/components/onboarding/TeacherLogin";
import { TeacherPasswordSetup } from "@/components/onboarding/TeacherPasswordSetup";
import { CMCApp } from "@/components/app/CMCAppLazy";
import {
  getOnboardingState,
  resetOnboarding,
  saveOnboardingState,
  saveTeacherCredentials,
  OnboardingState,
} from "@/lib/onboarding-store";
import { getAppState, saveAppState, buildAppStateFromOnboarding } from "@/lib/app-store";
import { quickLoginBeforeLogout, isSessionParked } from "@/lib/quick-login";
import { supabase } from "@/lib/supabase";
import { completeOnboardingHandoff } from "@/lib/onboarding-handoff";

type ViewState = "loading" | "onboarding" | "teacher-login" | "teacher-password" | "app";

const Index = () => {
  const navigate = useNavigate();
  const [onboardingState, setOnboardingState] = useState<OnboardingState | null>(null);
  const [viewState, setViewState] = useState<ViewState>("loading");

  // Temp state for re-login flow
  const [tempTeacher, setTempTeacher] = useState<{
    name: string;
    email: string;
    department: string;
  } | null>(null);

  // Supabase access token + user ID for authenticated teachers
  const [teacherAccessToken, setTeacherAccessToken] = useState<string | undefined>(() => {
    return sessionStorage.getItem('cmc_teacher_token') ?? undefined;
  });
  // Supabase user ID (UUID) for teachers — used as teacherId on lessons and Cosmos queries
  const [teacherUserId, setTeacherUserId] = useState<string | undefined>(() => {
    return sessionStorage.getItem('cmc_teacher_id') ?? undefined;
  });


  useEffect(() => {
    // Intercept Supabase invite/recovery hash tokens that land here because the
    // redirectTo URL isn't (yet) in Supabase's allowed-redirect-URLs list.
    // Supabase falls back to the site URL (/), but the hash tokens are still present.
    // We detect them immediately and navigate to the correct page before rendering anything.
    const hash = window.location.hash;
    if (hash) {
      const hp = new URLSearchParams(hash.substring(1));
      const type = hp.get('type');
      const accessToken = hp.get('access_token');
      if (accessToken && (type === 'invite' || type === 'recovery')) {
        window.location.replace(`/auth/reset-password${hash}`);
        return;
      }
    }

    // Preserve a cover-portal deep-link intent (from a cover-assignment email)
    // across any login redirect — a school admin with no active session gets
    // sent to /school/login, which would otherwise drop the ?openCoverPortal
    // query. CMCApp re-reads this on mount and opens the cover portal.
    const coverParams = new URLSearchParams(window.location.search);
    if (coverParams.get('openCoverPortal') === '1') {
      sessionStorage.setItem('cmc_pending_cover_portal', coverParams.get('lessonId') || '1');
    }
    // Same preservation for a ?openClasses=1 deep-link (retrieval-request email)
    // so an admin bounced through /school/login still lands on the class list.
    if (coverParams.get('openClasses') === '1') {
      sessionStorage.setItem('cmc_pending_classes', '1');
    }
    // A ?classId=<id> share/deep-link opens the class list scrolled to + flashing
    // that specific profile. Preserve it across the login bounce too.
    if (coverParams.get('classId')) {
      sessionStorage.setItem('cmc_pending_class_id', coverParams.get('classId') as string);
    }
    // ?openReview=1 (retention-review email) → the bulk review screen.
    if (coverParams.get('openReview') === '1') {
      sessionStorage.setItem('cmc_pending_review', '1');
    }

    const state = getOnboardingState();
    setOnboardingState(state);

    if (state.isComplete) {
      // Check if this is a first-ever login (teacher invite just accepted)
      // The first-login flag used to route teachers through the welcome
      // animation. That screen is gone, so it is only cleared here — kept rather
      // than deleted so a stale '1' can't leak into whatever reads it next.
      sessionStorage.removeItem('cmc_first_login');
      {
        setViewState("app");
      }
    } else if (state.userType === 'school') {
      if (state.supabaseUserId) {
        // School admin has completed setup before and their session ended — go to login.
        navigate('/school/login');
      } else {
        // School admin is mid-wizard (refreshed during setup) — resume the wizard.
        setViewState("onboarding");
      }
    } else if (state.userType === 'teacher' || state.userType === 'cover') {
      // Teacher or cover teacher — both log in with email + password via TeacherLogin.
      setViewState("teacher-login");
    } else {
      // Completely new user — play the CoverMyClass animation, which then routes
      // them to /school/login (the unified entry point with teacher option at the bottom).
      setViewState("onboarding");
    }
  }, []);

  // Recover the teacher's Supabase user id (+ token) from the live session when
  // we land straight in the app from persisted onboarding state — a fresh tab or
  // a reopen, where the tab-scoped `cmc_teacher_id` in sessionStorage is gone but
  // `onboardingState.isComplete` (localStorage) still says "logged-in teacher".
  // Without this, CMCApp receives userId=undefined, so new lesson drafts are
  // stamped teacherId:undefined — which the 30s teacher poll used to drop
  // ("Lost track of this lesson") and which also breaks lesson ownership on
  // submit and the "my lessons" filter. Cover teachers use a one-time token (no
  // Supabase session), so this is teacher-only; their id comes from teacherCode.
  useEffect(() => {
    if (onboardingState?.userType !== 'teacher' || !onboardingState?.isComplete) return;
    if (teacherUserId) return; // interactive login already populated it
    // Belt-and-braces with the quick-login contract: a "parked" session is a
    // logged-out teacher's session kept alive only for the one-tap card. Never
    // adopt its id here — the isComplete gate already keeps parked users on the
    // login screen, but guard locally so that guarantee doesn't depend on it.
    if (isSessionParked()) return;
    let cancelled = false;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled || !session?.user?.id) return;
      sessionStorage.setItem('cmc_teacher_id', session.user.id);
      setTeacherUserId(session.user.id);
      if (session.access_token) {
        sessionStorage.setItem('cmc_teacher_token', session.access_token);
        setTeacherAccessToken(session.access_token);
      }
    })();
    return () => { cancelled = true; };
  }, [onboardingState?.userType, onboardingState?.isComplete, teacherUserId]);

  // Stable reference — OnboardingWizard's useEffect([onComplete]) must not re-fire
  // on every Index render, or it will call onComplete() prematurely when isComplete
  // has been set to true before registration has actually finished.
  const handleOnboardingComplete = useCallback(() => {
    // Shared with DemoApp — see completeOnboardingHandoff for why this is not
    // written out inline any more. In short: this copy did not persist
    // isComplete, and the app branch below requires it, so any route that
    // skipped SetupComplete's own save left the user stranded on the wizard.
    setOnboardingState(completeOnboardingHandoff());
    setViewState("app");
  }, []);

  const handleLogout = async () => {
    // When quick-login is on, snapshot the account + sign out locally so the
    // one-tap card survives; otherwise a no-op (preserves prior behaviour).
    await quickLoginBeforeLogout();
    // Clear teacher session on logout
    sessionStorage.removeItem('cmc_teacher_token');
    sessionStorage.removeItem('cmc_teacher_id');
    setTeacherAccessToken(undefined);
    setTeacherUserId(undefined);

    // For teachers/cover: go back to login page, not full reset
    if (onboardingState?.userType === "teacher" || onboardingState?.userType === "cover") {
      const newState = {
        ...onboardingState,
        isComplete: false,
        teacherName: "",
        teacherEmail: "",
        teacherDepartment: "",
      };
      saveOnboardingState(newState);
      setOnboardingState(newState);
      setViewState("teacher-login");
    } else {
      // For school admin: mark as not complete and redirect to school login
      const newState = { ...onboardingState!, isComplete: false };
      saveOnboardingState(newState);
      setOnboardingState(newState);
      navigate('/school/login');
    }
  };

  // Teacher (and cover teacher) login handler
  const handleTeacherLogin = (teacherName: string, email: string, department: string, hasPassword: boolean, accessToken?: string, userId?: string, role?: string, schoolId?: string) => {
    // Store Supabase access token — used by both teachers and cover teachers
    if (accessToken) {
      sessionStorage.setItem('cmc_teacher_token', accessToken);
      setTeacherAccessToken(accessToken);
    }
    if (userId) {
      sessionStorage.setItem('cmc_teacher_id', userId);
      setTeacherUserId(userId);
    }

    if (role === 'cover') {
      // Cover teacher — set schoolId for config fetching, then show welcome
      if (schoolId) sessionStorage.setItem('cmc_cover_school_id', schoolId);
      const newState: OnboardingState = {
        ...onboardingState!,
        isComplete: true,
        userType: 'cover',
        teacherName,
        teacherEmail: email,
        teacherDepartment: department || '',
      };
      saveOnboardingState(newState);
      setOnboardingState(newState);
      setViewState("app");
      return;
    }

    // Standard teacher flow
    setTempTeacher({ name: teacherName, email, department });

    if (hasPassword) {
      const newState: OnboardingState = {
        ...onboardingState!,
        teacherName,
        teacherEmail: email,
        teacherDepartment: department,
        isComplete: true,
      };
      saveOnboardingState(newState);
      setOnboardingState(newState);
      setViewState("app");
    } else {
      setViewState("teacher-password");
    }
  };

  const handlePasswordSetup = (password: string) => {
    if (tempTeacher) {
      saveTeacherCredentials({
        code: onboardingState?.teacherCode || "",
        email: tempTeacher.email,
        password,
        name: tempTeacher.name,
        department: tempTeacher.department,
      });

      const newState: OnboardingState = {
        ...onboardingState!,
        teacherName: tempTeacher.name,
        teacherEmail: tempTeacher.email,
        teacherDepartment: tempTeacher.department,
        teacherPassword: password,
        isComplete: true,
      };
      saveOnboardingState(newState);
      setOnboardingState(newState);
      setViewState("app");
    }
  };

  // Boot cover. The cream brand splash that used to sit here was removed —
  // two full-screen intros before the app (cream splash, then the navy
  // welcome) was one too many, so the brand animation moved onto the navy
  // welcome screen and this is now just a quiet hold.
  //
  // It is deliberately NOT blank and NOT animated: blank would read as the
  // white-screen failure mode, and a second animation is the thing we just
  // removed. Same navy gradient as the welcome screen, so boot → welcome is
  // visually continuous with no flash between them.
  if (viewState === "loading" || onboardingState === null) {
    return (
      <div
        role="status"
        aria-label="CoverMyClass is loading"
        className="min-h-screen bg-hero-gradient"
      >
        <span className="sr-only">Loading…</span>
      </div>
    );
  }

  // Teacher (and cover teacher) re-login flow
  if (viewState === "teacher-login") {
    return (
      <TeacherLogin
        onLogin={handleTeacherLogin}
        onBack={() => {
          resetOnboarding();
          setOnboardingState(null);
          setViewState("onboarding");
          window.location.reload();
        }}
      />
    );
  }

  if (viewState === "teacher-password" && tempTeacher) {
    return <TeacherPasswordSetup teacherName={tempTeacher.name} onComplete={handlePasswordSetup} />;
  }

  // The teacher welcome animation is gone — ~5s of branded intro before the app
  // is not MVP work. The COVER path below still uses the same component; that is
  // a separate call, deliberately left alone.

  // Show the main app if onboarding is complete
  if (viewState === "app" && onboardingState.isComplete) {
    // Determine user type and name
    const userType = onboardingState.userType as "school" | "teacher" | "cover";
    const userName =
      userType === "school"
        ? `${onboardingState.schoolData.contactFirstName || ""} ${onboardingState.schoolData.contactLastName || ""}`.trim() ||
          "Admin"
        : onboardingState.teacherName || "Teacher";

    return (
      <CMCApp
        userType={userType}
        userName={userName}
        userId={
          userType === "teacher"
            ? (teacherUserId || onboardingState.teacherCode || undefined)
            : userType === "cover"
              ? (onboardingState.teacherCode || undefined)
              : "school-admin"
        }
        accessToken={userType === "teacher" || userType === "cover" ? teacherAccessToken : undefined}
        onLogout={handleLogout}
      />
    );
  }

  // Show onboarding wizard
  return <OnboardingWizard onComplete={handleOnboardingComplete} />;
};

const IndexWithBoundary = () => (
  <AppErrorBoundary>
    <Index />
  </AppErrorBoundary>
);

export default IndexWithBoundary;
